/**
 * What the "which inbox should Prest0n connect?" prompt does (Integrations part 1, PV13, 2026-10-11).
 *
 * Moved verbatim out of src/services/composio/panel/EmailIntentPrompt.tsx: the address (optional — skip connects
 * exactly as before), what counts as one, the firm's approval looked up 450 ms after the last keystroke (an
 * answer for an address since changed is dropped, and a failed lookup leaves the warning standing), Microsoft's
 * admin warning unless the firm already approved, and continue and skip. The prompt draws, on the Integrations
 * page and for the app-wide host alike; the new design's will read the same hook.
 *
 * Its frame is handed one steady close that always calls the host's latest cancel: the frame sets itself up again,
 * and takes the focus back to its card, whenever its close changes, and the Integrations page hands a new one every
 * time it renders (a focus re-read, every 6 s while a browser sign-in is unfinished), so the address field lost the
 * focus mid-word (found in Integrations part 1, 2026-10-11).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { checkTenantGrant } from '../../services/composio/ComposioClient';

export function useEmailIntentPrompt({ slug, onContinue, onCancel }: {
  slug: string;
  onContinue: (intendedAddress: string | null) => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const close = useCallback(() => cancelRef.current(), []);
  const [value, setValue] = useState('');
  // Firm-wide-approval memory: once someone at this firm got through the
  // admin wall, the NEXT person gets reassurance instead of the warning.
  // null = unknown (no address yet, lookup pending, or lookup failed).
  const [firmApproved, setFirmApproved] = useState<boolean | null>(null);
  const grantLookupSeq = useRef(0);
  const trimmed = value.trim();
  const valid = trimmed.length === 0 || (trimmed.includes('@') && trimmed.length <= 120);

  useEffect(() => {
    const seq = ++grantLookupSeq.current;
    if (!trimmed.includes('@') || trimmed.length > 120) {
      setFirmApproved(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      void checkTenantGrant(slug, trimmed)
        .then((granted) => {
          if (grantLookupSeq.current === seq) setFirmApproved(granted === true ? true : null);
        })
        .catch(() => { /* unknown stays unknown — the warning stands */ });
    }, 450);
    return () => clearTimeout(timer);
  }, [slug, trimmed]);

  const submit = () => {
    if (!valid) return;
    onContinue(trimmed.includes('@') ? trimmed : null);
  };

  return {
    value,
    setValue,
    /** The firm already approved Prest0n: reassurance instead of the warning. */
    firmApproved: firmApproved === true,
    /** Microsoft asks for an admin's approval: the warning, until the firm is known to have approved. */
    warnMicrosoft: slug === 'outlook' && firmApproved !== true,
    valid,
    submit,
    skip: () => onContinue(null),
    /** The frame's close (Escape, the backdrop): the same function every render, calling the host's latest cancel. */
    close,
  };
}
